import { Component, TemplateRef, ViewChild } from '@angular/core';
import { FormControl, FormGroup, Validators } from '@angular/forms';
import { NgbModal, NgbModalRef } from '@ng-bootstrap/ng-bootstrap';
import { ToastrService } from 'ngx-toastr';
import { finalize } from 'rxjs';
import { LicensingService } from './licensing.service';

@Component({
  selector: 'app-licensing',
  templateUrl: './licensing.component.html',
  styleUrls: ['./licensing.component.scss'],
})
export class LicensingComponent {
  public licensings: any[] = [];
  public total = 0;
  public page = 1;
  public pageSize = 20;

  public productOptions: any[] = [];

  public isInsert = false;
  public isSubmitted = false;
  public isLoaded = false;

  public selected: any = null;

  public form = new FormGroup({
    description: new FormControl<string>('', Validators.required),
    product: new FormControl<string>('', Validators.required),
    cnpj: new FormControl<string>('', Validators.required),
    razaoSocial: new FormControl<string>('', Validators.required),
    expiresAt: new FormControl<Date | null>(null, Validators.required),
  });

  public editForm = new FormGroup({
    description: new FormControl<string>('', Validators.required),
    expiresAt: new FormControl<Date | null>(null, Validators.required),
    active: new FormControl<boolean>(true, { nonNullable: true }),
  });
  public isEditSubmitted = false;

  public passwordValue = '';
  public passwordError = '';
  public passwordSubmitting = false;
  private pendingPayload: any = null;
  private pendingMode: 'create' | 'edit' | null = null;
  private editModalRef: NgbModalRef | null = null;
  private passwordModalRef: NgbModalRef | null = null;

  @ViewChild('passwordModal') passwordModalTpl!: TemplateRef<any>;

  constructor(
    private licensingService: LicensingService,
    private modalService: NgbModal,
    private toastr: ToastrService
  ) {
    this.loadLicensings();
    this.loadProducts();
  }

  loadLicensings(): void {
    this.isLoaded = false;
    this.licensingService
      .getLicensings(this.page, this.pageSize)
      .pipe(finalize(() => (this.isLoaded = true)))
      .subscribe({
        next: (res) => {
          this.licensings = res?.items ?? [];
          this.total = res?.total ?? 0;
        },
        error: (err) => {
          this.licensings = [];
          this.total = 0;
          this.toastr.error('Erro ao carregar licenciamentos');
          console.error(err);
        },
      });
  }

  loadProducts(): void {
    this.licensingService.getProducts(1, 1000).subscribe({
      next: (res) => {
        this.productOptions = (res?.items ?? []).filter((p: any) => p.active);
      },
      error: () => (this.productOptions = []),
    });
  }

  pageChange(): void {
    this.loadLicensings();
  }

  openInsert(): void {
    this.isInsert = true;
  }

  return(): void {
    this.isInsert = false;
    this.clearForm();
  }

  clearForm(): void {
    this.form.reset();
    this.isSubmitted = false;
  }

  add(): void {
    this.isSubmitted = true;
    if (!this.form.valid) return;

    const v = this.form.getRawValue();
    this.pendingPayload = {
      description: v.description,
      product: v.product,
      cnpj: this.onlyDigits(v.cnpj),
      razaoSocial: v.razaoSocial,
      expiresAt: this.toIsoDate(v.expiresAt),
      active: true,
    };
    this.pendingMode = 'create';
    this.openPasswordModal();
  }

  openEdit(modal: any, row: any): void {
    this.selected = row;
    this.isEditSubmitted = false;
    this.editForm.reset({
      description: row.description ?? '',
      expiresAt: this.toDateOrNull(row.expiresAt),
      active: !!row.active,
    });
    this.editModalRef = this.modalService.open(modal, { centered: true, size: 'lg' });
  }

  saveEdit(): void {
    this.isEditSubmitted = true;
    if (!this.editForm.valid || !this.selected) return;

    const v = this.editForm.getRawValue();
    this.pendingPayload = {
      id: this.selected.id,
      description: v.description,
      product: this.selected.product,
      cnpj: this.selected.cnpj,
      razaoSocial: this.selected.razaoSocial,
      expiresAt: this.toIsoDate(v.expiresAt),
      active: v.active,
    };
    this.pendingMode = 'edit';
    this.openPasswordModal();
  }

  private openPasswordModal(): void {
    this.passwordValue = '';
    this.passwordError = '';
    this.passwordSubmitting = false;
    this.passwordModalRef = this.modalService.open(this.passwordModalTpl, {
      backdrop: 'static',
      keyboard: true,
      centered: true,
      size: 'sm',
    });
  }

  cancelPassword(): void {
    this.passwordModalRef?.dismiss();
    this.passwordModalRef = null;
    this.pendingPayload = null;
    this.pendingMode = null;
  }

  confirmPassword(): void {
    if (this.passwordSubmitting) return;
    if (!this.passwordValue?.trim()) {
      this.passwordError = 'Informe a senha';
      return;
    }
    if (!this.pendingPayload || !this.pendingMode) return;

    this.passwordSubmitting = true;
    this.passwordError = '';

    const payload = { ...this.pendingPayload, password: this.passwordValue };
    const mode = this.pendingMode;
    this.isLoaded = false;

    this.licensingService
      .save(payload)
      .pipe(finalize(() => {
        this.isLoaded = true;
        this.passwordSubmitting = false;
      }))
      .subscribe({
        next: () => {
          this.passwordModalRef?.close();
          this.passwordModalRef = null;
          this.pendingPayload = null;
          this.pendingMode = null;
          if (mode === 'create') {
            this.toastr.success('Licenciamento criado com sucesso!');
            this.return();
            this.page = 1;
          } else {
            this.toastr.success('Licenciamento atualizado com sucesso!');
            this.editModalRef?.close();
            this.editModalRef = null;
          }
          this.loadLicensings();
        },
        error: (err) => {
          const msg = err?.error?.mensagem || 'Senha inválida ou erro ao salvar';
          this.passwordError = msg;
          console.error(err);
        },
      });
  }

  private toIsoDate(value: any): string {
    if (!value) return '';
    const d = value instanceof Date ? value : new Date(value);
    if (isNaN(d.getTime())) return '';
    return d.toISOString();
  }

  isValidDate(value: any): boolean {
    if (!value) return false;

    const raw = (value ?? '').toString();
    if (raw.startsWith('0001-01-01')) return false;

    const d = value instanceof Date ? value : new Date(value);
    return !isNaN(d.getTime());
  }

  private toDateOrNull(value: any): Date | null {
    if (!this.isValidDate(value)) return null;

    const d = value instanceof Date ? value : new Date(value);
    return isNaN(d.getTime()) ? null : d;
  }

  private onlyDigits(value: string | null | undefined): string {
    return (value ?? '').replace(/\D/g, '');
  }

  productName(id: string): string {
    const p = this.productOptions.find((x) => x.id === id);
    return p?.name ?? id;
  }
}
